/**
 * Shell-detection seam. The desktop app wraps the *exact same* Vite bundle that ships to the web, so
 * the client must decide web-vs-Tauri behavior at runtime. `isTauri()` from `@tauri-apps/api/core` is
 * the officially-supported check (it probes `window.__TAURI_INTERNALS__`); on the web it simply
 * returns false and the Tauri code paths are never entered.
 *
 * Keep Tauri-specific implementations (deep-link OAuth capture, loopback login) behind DYNAMIC imports
 * from the call sites so the web bundle never eagerly pulls native-only plugin code. This module is
 * the single place that imports the Tauri core API.
 */
import { isTauri } from '@tauri-apps/api/core';

export { isTauri };

/** True on the web build (browser) — the inverse of {@link isTauri}, spelled out for readability. */
export function isWeb(): boolean {
  return !isTauri();
}

/**
 * True on the Android shell. Both Android and macOS run under Tauri, but they need different sign-in
 * flows (Android: server-brokered poll; desktop: loopback OAuth), so callers branch on this. The
 * Android System WebView UA always contains "Android"; the macOS WKWebView never does.
 */
export function isAndroid(): boolean {
  return typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '');
}

/**
 * True on the iOS shell (iPhone/iPad under Tauri). Like {@link isAndroid}, iOS runs under Tauri but a
 * WKWebView can't do Firebase's popup, so iOS uses the same server-brokered sign-in as Android (see
 * {@link isMobile}). The iOS WKWebView UA contains "iPhone"/"iPad"; the `maxTouchPoints` fallback
 * catches iPadOS, which reports itself as "Macintosh" (the same case {@link isMacOS} excludes).
 */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return /Mac/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
}

/**
 * True on either mobile shell (Android or iOS). Callers branch on this to (a) use the server-brokered
 * sign-in instead of the desktop loopback OAuth, and (b) skip desktop-only plugins (updater, process,
 * global-shortcut) that aren't compiled into the mobile binaries (they're `#[cfg(desktop)]`).
 */
export function isMobile(): boolean {
  return isAndroid() || isIOS();
}

/** Public download URL for the signed, notarized macOS app (hosted in the lapo-downloads GCS bucket). */
export const MAC_APP_DOWNLOAD_URL = 'https://storage.googleapis.com/lapo-downloads-490015/Lapo.dmg';

/** Best-effort "is this a Mac desktop browser" check (excludes iPhone/iPad — including iPadOS in
 * desktop mode, which reports itself as "Macintosh" but has touch points, unlike a real Mac). */
export function isMacOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.platform || '';
  const ua = navigator.userAgent || '';
  const looksMac = /Mac/i.test(platform) || /Mac OS X/i.test(ua);
  if (!looksMac) return false;
  const isTouchApple = /iPhone|iPad|iPod/i.test(ua) || (navigator.maxTouchPoints ?? 0) > 1;
  return !isTouchApple;
}

/**
 * The macOS download URL to suggest to a web visitor — only when running in a browser (not already the
 * desktop app) on a Mac. `undefined` otherwise, so the UI can simply omit the suggestion.
 */
export function macDownloadSuggestion(): string | undefined {
  return isWeb() && isMacOS() ? MAC_APP_DOWNLOAD_URL : undefined;
}
